// /frontend/src/pages/admin/AdminDashboard.jsx
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import { 
  FaBoxOpen, 
  FaUsers, 
  FaShoppingCart, 
  FaMoneyBillAlt,
  FaShoppingBag,
  FaPlus,
  FaChartLine
} from 'react-icons/fa';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalUsers: 0,
    totalOrders: 0,
    revenue: 0,
    recentProducts: [],
    recentOrders: []
  });
  
  const [loading, setLoading] = useState(true);
  
  // Fetch dashboard stats - this would normally be an API call
  useEffect(() => {
    // Simulate API call with static data for now
    setTimeout(() => {
      setStats({
        totalProducts: 24,
        totalUsers: 48,
        totalOrders: 12,
        revenue: 12540.80,
        recentProducts: [
          { _id: '1', name: 'Magnus Extendable Dining Table', price: 1499, category: '1-bedroom' },
          { _id: '2', name: 'Astrid Wood Sideboard', price: 1099, category: '1-bedroom' },
          { _id: '3', name: 'Counter Stool Style 1', price: 399, category: '1-bedroom' },
          { _id: '4', name: 'Scandinavian Sofa', price: 1899, category: '2-bedroom' },
        ],
        recentOrders: [
          { _id: 'ORD123', date: '2023-05-10', customer: 'John Doe', total: 1578.90, status: 'delivered' },
          { _id: 'ORD124', date: '2023-05-09', customer: 'Jane Smith', total: 899.00, status: 'processing' },
          { _id: 'ORD125', date: '2023-05-08', customer: 'Mike Johnson', total: 2145.50, status: 'pending' },
          { _id: 'ORD126', date: '2023-05-07', customer: 'Sarah Williams', total: 399.00, status: 'delivered' },
        ]
      });
      setLoading(false);
    }, 1000);
  }, []);
  
  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-800"></div>
        </div>
      </AdminLayout>
    );
  }
  
  return (
    <AdminLayout>
      {/* Stats overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard 
          title="Total Products" 
          value={stats.totalProducts} 
          icon={<FaBoxOpen className="text-teal-600" />} 
          link="/admin/products"
        />
        <StatCard 
          title="Total Users" 
          value={stats.totalUsers} 
          icon={<FaUsers className="text-indigo-600" />} 
          link="/admin/users"
        />
        <StatCard 
          title="Orders" 
          value={stats.totalOrders} 
          icon={<FaShoppingCart className="text-amber-600" />} 
          link="/admin/orders"
        />
        <StatCard 
          title="Revenue" 
          value={`$${stats.revenue.toLocaleString()}`} 
          icon={<FaMoneyBillAlt className="text-emerald-600" />} 
          link="/admin/sales"
        />
      </div>
      
      {/* Quick actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <QuickActionCard 
          title="Add New Product" 
          description="Create a new product listing"
          icon={<FaShoppingBag />}
          buttonText="Add Product"
          buttonLink="/admin/products/new"
          color="bg-teal-600"
        />
        <QuickActionCard 
          title="Manage Inventory" 
          description="Update stock levels and variants"
          icon={<FaBoxOpen />}
          buttonText="View Inventory"
          buttonLink="/admin/inventory"
          color="bg-indigo-600"
        />
        <QuickActionCard 
          title="View Sales Report" 
          description="See detailed sales analytics"
          icon={<FaChartLine />}
          buttonText="View Reports"
          buttonLink="/admin/sales"
          color="bg-amber-600"
        />
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent products */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-800">Recent Products</h2>
            <Link to="/admin/products" className="text-sm text-teal-700 hover:text-teal-900 font-medium">
              View All
            </Link>
          </div>
          
          <div className="divide-y divide-gray-200">
            {stats.recentProducts.map((product) => (
              <div key={product._id} className="px-6 py-4 hover:bg-gray-50">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-sm font-medium text-gray-800">{product.name}</h3>
                    <p className="text-xs text-gray-500">Category: {product.category}</p>
                  </div>
                  <div className="text-sm font-medium text-gray-900">${product.price}</div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="px-6 py-3 bg-gray-50">
            <Link
              to="/admin/products/new"
              className="flex items-center justify-center text-sm text-teal-700 hover:text-teal-900 font-medium"
            >
              <FaPlus className="mr-1 h-3 w-3" /> Add New Product
            </Link>
          </div>
        </div>
        
        {/* Recent orders */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-800">Recent Orders</h2>
            <Link to="/admin/orders" className="text-sm text-teal-700 hover:text-teal-900 font-medium">
              View All
            </Link>
          </div>
          
          <div className="divide-y divide-gray-200">
            {stats.recentOrders.map((order) => (
              <div key={order._id} className="px-6 py-4 hover:bg-gray-50">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-sm font-medium text-gray-800">{order._id}</h3>
                    <p className="text-xs text-gray-500">{order.customer} • {order.date}</p>
                  </div>
                  <div className="flex items-center">
                    <div className="mr-4 text-sm font-medium text-gray-900">${order.total}</div>
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      order.status === 'delivered' ? 'bg-green-100 text-green-800' :
                      order.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="px-6 py-3 bg-gray-50">
            <Link
              to="/admin/orders"
              className="flex items-center justify-center text-sm text-teal-700 hover:text-teal-900 font-medium"
            >
              View More Orders
            </Link>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

// Stat card component
const StatCard = ({ title, value, icon, link }) => (
  <Link to={link} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
    <div className="flex justify-between items-center mb-4">
      <h3 className="text-sm font-medium text-gray-500">{title}</h3>
      <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center">
        {icon}
      </div>
    </div>
    <div className="text-2xl font-semibold text-gray-900">{value}</div>
  </Link>
);

// Quick action card component
const QuickActionCard = ({ title, description, icon, buttonText, buttonLink, color }) => (
  <div className="bg-white rounded-lg shadow-md overflow-hidden">
    <div className="p-6">
      <div className={`h-12 w-12 rounded-lg ${color} text-white flex items-center justify-center mb-4`}>
        {icon}
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-1">{title}</h3>
      <p className="text-sm text-gray-600 mb-4">{description}</p>
      <Link 
        to={buttonLink}
        className="block w-full text-center py-2 px-4 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
      >
        {buttonText}
      </Link>
    </div>
  </div>
);

export default AdminDashboard;